// FlixPlayerZIndexChecker.jsx
// Leaker detection component
// If user tries to update video element's css position, app would crash
// If `props.isSuspended` is `ture`, crashing will be suspended until it turns to `false`.

import React from 'react';
import PropTypes from 'prop-types';

import trackMixpanel from '../resource/mixpanel.js';
import { EventTypes } from '../resource/trackingConstants.js';
import { leakerMessage } from '../resource/leaker.js';

export class FlixPlayerZIndexChecker extends React.PureComponent {
  intervalHandle = null;
  isTriggerPostponed = false;
  postponedTrackArgs = null;
  triggerTimeoutHandle = null;

  /** Track user's leaker behavior */
  track = ({ zIndexes, positions, isVideoElementMovedOut }) => {
    trackMixpanel({
      type: EventTypes.USER_FLIX_TRY_MODIFY_Z_INDEX,
      payload: {
        zIndexes,
        positions,
        isVideoElementMovedOut,
      },
    });
  };

  /** Crash app in random seconds */
  trigger = trackArgs => {
    const { messageId } = this.props;
    // In case of user was tracked when logged-out
    if (this.isTriggerPostponed) {
      this.track(trackArgs);
    }
    // Crash React in random callstack in random seconds
    this.triggerTimeoutHandle = setTimeout(
      () => {
        React.PureComponent.prototype.componentDidUpdate = () => {
          throw new Error(
            leakerMessage({ message: `messageId: ${messageId}` })
          );
        };
      },
      (Math.random() * 10 + 5) * 1000
    );
  };

  check = () => {
    const { player, isSuspended } = this.props;

    /** @type {HTMLElement} */
    const playerElement = player?.el_;

    if (!playerElement) return;

    const videoElement = playerElement.querySelector('video');

    let shouldTrigger = false;
    let trackArgs = null;

    if (videoElement) {
      // Trigger if style.position is changed
      if (window.getComputedStyle(videoElement).position !== 'static') {
        shouldTrigger = true;
        trackArgs = {
          zIndexes: [],
          positions: [],
          isVideoElementMovedOut: false,
        };
        // collect zIndexes and positions for tracking
        let currentNode = videoElement;
        while (currentNode && currentNode != document.documentElement) {
          const computedStyle = window.getComputedStyle(currentNode);
          trackArgs.zIndexes.push(computedStyle.zIndex);
          trackArgs.positions.push(computedStyle.position);
          currentNode = currentNode.parentNode;
        }
      }
    } else {
      // Video is moved outside of player's div element
      shouldTrigger = true;
      trackArgs = {
        zIndexes: [],
        positions: [],
        isVideoElementMovedOut: true,
      };
    }

    if (shouldTrigger) {
      this.track(trackArgs);
      if (isSuspended) {
        this.isTriggerPostponed = true;
        this.postponedTrackArgs = trackArgs;
      } else {
        this.trigger(trackArgs);
      }
      clearInterval(this.intervalHandle);
    }
  };

  componentDidMount() {
    this.intervalHandle = setInterval(this.check, 1000);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.isSuspended &&
      !this.props.isSuspended &&
      this.isTriggerPostponed
    ) {
      this.trigger(this.postponedTrackArgs);
    }
  }

  componentWillUnmount() {
    clearInterval(this.intervalHandle);
    clearTimeout(this.triggerTimeoutHandle);
  }

  render() {
    return null;
  }
}

FlixPlayerZIndexChecker.propTypes = {
  isSuspended: PropTypes.bool.isRequired,
  player: PropTypes.object,
  messageId: PropTypes.string,
};

FlixPlayerZIndexChecker.defaultProps = {
  messageId: '',
};

export default FlixPlayerZIndexChecker;
